import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { styled } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';
import { Grid } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { LoadingIndicator } from '../LoadingIndicator';
import { Text } from '../typography';
const FLAG_SIZE = 'clamp(65px, 52%, 130px)';
const FlagableGrid = styled(Grid, {
    shouldForwardProp: (prop) => !['isZoomable', 'showFlag'].includes(prop),
}) `
  overflow: hidden;
${({ isZoomable, showFlag }) => isZoomable && `
  &::before {
    content: "";
    position: absolute;
    display: ${showFlag ? 'block' : 'none'};
    width: ${FLAG_SIZE};
    height: ${FLAG_SIZE};
    background-color: rgba(255, 255, 255, 0.8);
    top: 0;
    right: 0;
    transform: translate(50%, -50%) rotate(45deg);
    z-index: 2;
  }
`}`;
const StyledImg = styled('img') `
  width: 100%;
  height: 100%;
  overflow: auto;
`;
/**
 * ItemPhoto component  displays an image or a color swatch. It can be zoomed in on click.
 *
 * This is a base-image element; if you want to have a functional zoom modal appear,
 * you will want to use the `ImageWithZoom` component instead.
 *
 * Note that `hexUrl` will have priority over `imageUrl` if both are provided.
 */
const ItemPhoto = ({ imageUrl = '', className = '', hexCode, hexUrl, alt, isZoomable: isZoomableProp = true, onLoad, onError, onZoomClick, size = 'normal', fit = 'contain', width, height, minWidth, maxWidth, zoomIconSize, showWhenNoPhotoAvailable = false, unavailableText = 'Item photo unavailable', clickImageToZoom = false, borderRadius = false, sx = {}, showFlag = true, }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const hasUrl = !!imageUrl || !!hexUrl;
    const shouldUseImage = hasUrl && !hexCode;
    const hasNothingToDisplay = !hasUrl && !hexCode;
    const hasZoomableImage = !!imageUrl && !hexUrl && !hexCode;
    if (!showWhenNoPhotoAvailable && (hasNothingToDisplay || error)) {
        return null;
    }
    const isZoomable = isZoomableProp && !error && hasZoomableImage;
    const handleLoad = () => {
        setLoaded(true);
        setError(false);
        onLoad?.();
    };
    const handleError = () => {
        setError(true);
        setLoaded(false);
        onError?.();
    };
    const handleZoomClick = (event) => {
        event.stopPropagation();
        (isZoomable && loaded) && onZoomClick?.();
    };
    const sizeMap = {
        small: {
            image: '104px',
            icon: 'small',
            iconPadding: '5px',
            displayNoPhotoText: false,
        },
        medium: {
            image: '160px',
            icon: 'medium',
            iconPadding: '6px',
            displayNoPhotoText: false,
        },
        normal: {
            image: '320px',
            icon: 'large',
            iconPadding: '10px',
            displayNoPhotoText: true,
        },
    };
    let sizeValues;
    if (typeof size === 'string') {
        sizeValues = sizeMap[size];
    }
    else {
        sizeValues = { ...sizeMap.normal, ...size };
    }
    const getBackgroundColor = () => {
        if (hexCode) {
            return hexCode;
        }
        return error || !hasUrl ? 'action.disabledBackground' : 'background.default';
    };
    let borderRadiusValue = ({ borderRadius: !borderRadius ? 0 : '4px' });
    if (typeof borderRadius === 'object') {
        let borderValues = '';
        borderValues = `${borderRadius.topLeft ? '4px' : '0'} ${borderRadius.topRight ? '4px' : '0'}
    ${borderRadius.bottomLeft ? '4px' : '0'} ${borderRadius.bottomRight ? '4px' : '0'}`;
        borderRadiusValue = ({ borderRadius: borderValues });
    }
    return (_jsxs(FlagableGrid, { className: className || (!error ? 'item-photo-container' : undefined), bgcolor: getBackgroundColor(), sx: {
            position: 'relative',
            height: height ?? sizeValues.image,
            minWidth: minWidth ?? (maxWidth ? undefined : sizeValues.image),
            maxWidth: maxWidth,
            width: width ?? sizeValues.image,
            maxHeight: sizeValues.image,
            overflow: 'hidden',
            borderRadius: hexCode ? '4px 4px 0 0' : '0', // utilized for color swatches in Style card
            ...sx,
        }, container: true, display: "flex", direction: "column", justifyContent: "center", alignItems: "center", isZoomable: isZoomable, "data-testid": "item-photo", onClick: clickImageToZoom ? handleZoomClick : undefined, showFlag: showFlag, children: [(!error && shouldUseImage) && (_jsxs(_Fragment, { children: [_jsx(StyledImg, { src: hexUrl || imageUrl, alt: alt, onLoad: handleLoad, onError: handleError, loading: "lazy", style: {
                            opacity: loaded ? '1' : '0',
                            objectFit: fit,
                        }, sx: {
                            ...borderRadiusValue,
                            cursor: isZoomable ? 'zoom-in' : 'default',
                        }, className: "item-photo" }), (isZoomable && loaded) &&
                        _jsx(IconButton, { className: "zoom-icon", sx: {
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                p: sizeValues.iconPadding,
                                zIndex: 4,
                            }, onClick: handleZoomClick, children: _jsx(ZoomInIcon, { fontSize: zoomIconSize || sizeValues?.icon, color: 'action' }) })] })), (shouldUseImage && !loaded && !error) &&
                _jsx(LoadingIndicator, { sx: { position: 'absolute' } }), ((error && shouldUseImage) || hasNothingToDisplay) &&
                _jsxs(_Fragment, { children: [_jsx(NoPhotographyIcon, { fontSize: sizeValues?.icon, color: 'disabled' }), sizeValues.displayNoPhotoText &&
                            _jsx(Text, { color: 'text.disabled', children: unavailableText })] })] }));
};
export default ItemPhoto;
//# sourceMappingURL=index.js.map